/**
 * AK-584 — мастер первого входа: «три шага за руку» до первого поля.
 *
 * Показывается сразу после `/onboarding/set-password`, когда в ТЕНАНТЕ нет ни
 * одного поля (не «первый вход этого человека» — второму агроному того же
 * клиента мастер не положен).
 *
 * Р1: шаги 1-2 — отдельный экран (как «Придумайте пароль»), шаг 3 — возврат в
 *     обычный интерфейс: «Готово» показывается уже на карте, рядом с полем.
 * Р2: шаг «создайте хозяйство» сам себя пропускает, если хозяйство уже есть.
 *     Хозяйств несколько → на шаге поля появляется выбор, в какое рисовать.
 * Р3: «Открыть карту» — главная кнопка, «Загрузить файл с полями» — рядом и
 *     равноправная (у клиента с 200 полями рисовать по одному — издевательство).
 *
 * Мастер пропускаемый: «Пропустить настройку» уводит в обычный интерфейс, где
 * про незаконченную настройку напоминает янтарный баннер (SetupIncompleteBanner),
 * а вернуться можно через `/getting-started`.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Check, Loader2, Map as MapIcon, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { safeNext } from '@/pages/onboarding/set-password';
import {
  setupStep,
  shouldPickFarm,
  type SetupStep,
} from '@/lib/onboarding/setupState';
import {
  useInvalidateSetupStatus,
  useSetupStatus,
} from '@/lib/onboarding/useSetupStatus';

interface FarmRow {
  id: string;
  name: string;
  cluster_id: string | null;
}
interface ClusterRow {
  id: string;
  name: string;
}

const NO_CLUSTER = '__none__';

/** Шкала «шаг N из 3»: пройденные — галочкой, текущий — янтарём. */
function StepScale({ current }: { current: 1 | 2 | 3 }) {
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      {([1, 2, 3] as const).map((n) => (
        <div key={n} className="flex flex-1 items-center gap-2">
          <span
            aria-hidden
            className={cn(
              'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold',
              n < current && 'border-primary bg-primary/10 text-primary',
              n === current &&
                'border-accent-brand bg-accent-brand/10 text-accent-brand',
              n > current && 'border-border bg-muted text-muted-foreground',
            )}
          >
            {n < current ? <Check className="h-3 w-3" /> : n}
          </span>
          {n < 3 && <span className="h-px flex-1 bg-border" />}
        </div>
      ))}
      <span className="shrink-0">шаг {current} из 3</span>
    </div>
  );
}

export function FirstRunWizardPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/onboarding/first-run' }) as {
    next?: string;
  };
  const next = safeNext(search.next);

  const { counts, isLoading } = useSetupStatus();
  const invalidateSetup = useInvalidateSetupStatus();

  const [farms, setFarms] = useState<FarmRow[]>([]);
  const [clusters, setClusters] = useState<ClusterRow[]>([]);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [refsLoading, setRefsLoading] = useState(true);

  // Шаг 1 — создание хозяйства.
  const [farmName, setFarmName] = useState('');
  const [clusterId, setClusterId] = useState<string>(NO_CLUSTER);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Шаг 2 — в какое хозяйство рисовать (нужен выбор только если их несколько).
  const [targetFarmId, setTargetFarmId] = useState<string>('');

  // Локальный шаг: после создания хозяйства мы уже знаем, что идём на шаг 2,
  // не дожидаясь ре-фетча сводки.
  const [forcedStep, setForcedStep] = useState<SetupStep | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const [fr, cl, co] = await Promise.all([
        api.get<FarmRow[]>('/api/v1/farms').catch(() => [] as FarmRow[]),
        api.get<ClusterRow[]>('/api/v1/clusters').catch(() => [] as ClusterRow[]),
        api
          .get<{ id: string }[]>('/api/v1/companies')
          .catch(() => [] as { id: string }[]),
      ]);
      if (!alive) return;
      setFarms(fr);
      setClusters(cl);
      setCompanyId(co[0]?.id ?? null);
      setRefsLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Шаг считаем по составу тенанта: поля — из /scope/available (не зависят от
  // активного скоупа сессии), хозяйства и компания — из своих же справочников,
  // которые всё равно нужны форме.
  const step =
    forcedStep ??
    setupStep(
      counts
        ? {
            farms: farms.length,
            fields: counts.fields,
            has_company: companyId !== null,
          }
        : null,
    );

  // Поля уже есть — мастер не нужен, уходим туда, куда шли.
  useEffect(() => {
    if (isLoading || step !== 'done') return;
    window.location.replace(next);
  }, [isLoading, step, next]);

  /**
   * Единственное хозяйство подставляем сами (Р2), иначе ждём выбор.
   *
   * Считаем ВЫЧИСЛЕНИЕМ, а не эффектом: с эффектом первый кадр шага «поле»
   * рисовался с пустым хозяйством, и кнопки «Открыть карту» / «Загрузить
   * файл» успевали побыть неактивными — быстрый клик проваливался в никуда
   * (поймано красным CI, локально гонка не воспроизводилась).
   */
  const effectiveFarmId = useMemo(() => {
    if (targetFarmId) return targetFarmId;
    if (farms.length === 0 || shouldPickFarm(farms.length)) return '';
    return farms[0].id;
  }, [targetFarmId, farms]);

  const targetFarm = useMemo(
    () => farms.find((f) => f.id === effectiveFarmId) ?? null,
    [farms, effectiveFarmId],
  );

  const skip = () => {
    window.location.replace(next);
  };

  const goToMap = (withImport: boolean) => {
    navigate({
      to: '/map',
      search: {
        addField: 1,
        ...(withImport ? { import: 1 } : {}),
        ...(effectiveFarmId ? { farm: effectiveFarmId } : {}),
        onboarding: 1,
      },
    });
  };

  const createFarm = async () => {
    const name = farmName.trim();
    if (!name) {
      setError('Введите название хозяйства.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      if (!companyId) {
        setError('Компания ещё не заведена — обратитесь в поддержку.');
        return;
      }
      const created = await api.post<FarmRow>('/api/v1/farms', {
        company_id: companyId,
        name,
        cluster_id: clusterId === NO_CLUSTER ? null : clusterId,
      });
      setFarms((prev) => [...prev, created]);
      setTargetFarmId(created.id);
      setForcedStep('field');
      invalidateSetup();
    } catch (e) {
      setError(
        e instanceof ApiError ? e.detail : 'Не удалось создать хозяйство.',
      );
    } finally {
      setCreating(false);
    }
  };

  if (isLoading || refsLoading || step === 'done') {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-secondary/40 p-6">
        <div
          className="flex items-center gap-2 text-sm text-muted-foreground"
          data-testid="first-run-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Загружаем…
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-secondary/40 p-4 sm:p-6">
      <Card className="w-full max-w-lg" data-testid="first-run-wizard">
        <CardHeader className="p-4 pb-3">
          <StepScale current={step === 'field' ? 2 : 1} />
          <CardTitle className="mt-3 text-xl font-semibold">
            {step === 'field' ? 'Теперь добавим поле' : 'Начнём с хозяйства'}
          </CardTitle>
          <CardDescription>
            {step === 'field'
              ? 'Откроем карту. Там можно тапнуть по полю и границы найдутся сами, обвести контур вручную или загрузить файл с полями.'
              : 'Хозяйство — это участок дела, к которому привязаны поля: бригада, отделение, юрлицо-производитель. Позже их может быть несколько.'}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-4 p-4 pt-0">
          {step === 'company' && (
            <>
              <p className="text-sm text-muted-foreground">
                Сначала нужна компания — к ней привязываются хозяйства и поля.
              </p>
              <Button
                data-testid="first-run-create-company"
                onClick={() => navigate({ to: '/onboarding/company' })}
              >
                Создать компанию
              </Button>
            </>
          )}

          {step === 'farm' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="fr-farm-name">Название хозяйства</Label>
                <Input
                  id="fr-farm-name"
                  data-testid="first-run-farm-name"
                  autoFocus
                  value={farmName}
                  maxLength={500}
                  placeholder="напр. «Северное»"
                  onChange={(e) => setFarmName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void createFarm();
                  }}
                />
              </div>

              {clusters.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="fr-cluster">
                    Кластер{' '}
                    <span className="font-normal text-muted-foreground">
                      · необязательно
                    </span>
                  </Label>
                  <Select value={clusterId} onValueChange={setClusterId}>
                    <SelectTrigger id="fr-cluster" data-testid="first-run-cluster">
                      <SelectValue placeholder="Не выбран" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_CLUSTER}>Не выбран</SelectItem>
                      {clusters.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button
                data-testid="first-run-create-farm"
                disabled={creating || !farmName.trim()}
                onClick={() => void createFarm()}
              >
                {creating && <Loader2 className="h-4 w-4 animate-spin" />}
                Создать и продолжить
              </Button>
            </>
          )}

          {step === 'field' && (
            <>
              {shouldPickFarm(farms.length) ? (
                <div className="space-y-2">
                  <Label htmlFor="fr-target-farm">Поле попадёт в хозяйство</Label>
                  <Select value={effectiveFarmId} onValueChange={setTargetFarmId}>
                    <SelectTrigger
                      id="fr-target-farm"
                      data-testid="first-run-target-farm"
                    >
                      <SelectValue placeholder="Выберите хозяйство" />
                    </SelectTrigger>
                    <SelectContent>
                      {farms.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                targetFarm && (
                  <div
                    data-testid="first-run-farm-fact"
                    className="rounded-md border border-dashed border-border bg-muted/40 px-3 py-2"
                  >
                    <div className="text-xs text-muted-foreground">
                      Поле попадёт в хозяйство
                    </div>
                    <div className="text-sm font-medium text-foreground">
                      {targetFarm.name}
                    </div>
                  </div>
                )
              )}

              {/* Р3: обе кнопки равноправны — «нарисовать» и «загрузить файл». */}
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  variant="accent-brand"
                  className="flex-1"
                  data-testid="first-run-open-map"
                  disabled={!effectiveFarmId}
                  onClick={() => goToMap(false)}
                >
                  <MapIcon className="h-4 w-4" />
                  Открыть карту
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  data-testid="first-run-import"
                  disabled={!effectiveFarmId}
                  onClick={() => goToMap(true)}
                >
                  <Upload className="h-4 w-4" />
                  Загрузить файл с полями
                </Button>
              </div>
            </>
          )}

          {error && (
            <p className="text-xs text-destructive" data-testid="first-run-error">
              {error}
            </p>
          )}

          <Button
            variant="ghost"
            size="sm"
            className="self-center text-muted-foreground"
            data-testid="first-run-skip"
            onClick={skip}
          >
            Пропустить настройку
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default FirstRunWizardPage;
